import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalAtom } from "../../../components/atoms/atom";
import PermissionPresetSelector from "../../../components/project/PermissionPresetSelector";
import {
	isTradeAccessIncomplete,
	normalizePermissions,
} from "../../../constant/projectPermissions";
import { usePromoteAdminMutation } from "../../../store/services/projectMembers";
import { showToast } from "../../../utils/method";
import PermissionsForm from "../_shared/permissions-form";

/**
 * Demoting a project admin clears the admin flag, which leaves the member with
 * no permissions of their own. So instead of a plain confirm, we open the
 * permission picker and require the actor to choose the member's new Custom
 * permissions; those are sent with the demotion.
 */
function DemoteAdminModal({ show, onClose, member, projectId, onSuccess }) {
	const { t } = useTranslation();
	const [permissions, setPermissions] = useState(null);
	const [presetUsedName, setPresetUsedName] = useState(null);

	const [promoteAdmin, { isLoading }] = usePromoteAdminMutation();

	useEffect(() => {
		if (show && member) {
			// Start from the default member permission set as a sensible base.
			setPermissions(normalizePermissions(null));
			setPresetUsedName(null);
		}
	}, [show, member]);

	const handleSave = async () => {
		if (!member?.id) return;
		try {
			await promoteAdmin({
				projectId,
				memberId: member.id,
				isProjectAdmin: false,
				permissions: normalizePermissions(permissions),
				presetUsedName,
			}).unwrap();
			showToast(t("projects.members.demote-admin-success"), {
				type: "success",
			});
			onSuccess?.();
			onClose();
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	return (
		<ModalAtom
			show={show}
			handleClose={onClose}
			size="lg"
			title={t("projects.members.demote-admin")}
			saveText={isLoading ? "common.saving" : "projects.members.demote-admin"}
			disabled={isLoading || !permissions}
			submitDisabled={isTradeAccessIncomplete(permissions)}
			handleSubmit={handleSave}
			body={
				permissions && (
					<>
						<p className="text-muted">
							{t(
								"projects.members.demote-admin-choose-permissions",
								{
									name:
										member?.user?.fullName ||
										member?.user?.email ||
										"",
								},
							)}
						</p>
						<PermissionPresetSelector
							projectId={projectId}
							appliedPermissions={permissions}
							onPresetApplied={({
								presetUsedName: name,
								permissions: perms,
							}) => {
								setPresetUsedName(name);
								setPermissions(perms);
							}}
						/>
						<PermissionsForm
							projectId={projectId}
							permissions={permissions}
							onChange={setPermissions}
						/>
					</>
				)
			}
		/>
	);
}

export default DemoteAdminModal;
